import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Tag, Button, message, Form, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { usePermissions } from '../../contexts/PermissionContext';
import permissionServiceAPI from '../../api/permissionService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTableQuery } from '../../hooks/useTableQuery';
import { getProjectList, getProjectGroupList, getCustomerList } from '../../services/project';
import type { Project, ProjectListParams, ProjectGroup, ProjectFilters } from '../../types/project';
import ProjectFilter from './components/ProjectFilter';
import ProjectTable from './components/ProjectTable';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isMainAccount, hiddenFields } = usePermissions();
  const hidden = hiddenFields('project.Project');
  const strategyHideCustomer = Array.isArray(hidden) && hidden.includes('customer__username');
  const isCustomerRole = user?.role === 'customer';
  const hideCustomer = strategyHideCustomer || isCustomerRole;
  console.info('[ProjectList] hiddenFields(project.Project)=', hidden, 'hideCustomer=', hideCustomer);
  // 统一使用权限画像判断；为兼容主账号权限画像未完善，保留 isMainAccount 兜底
  const canCreateProject = hasPermission('project:create') || isMainAccount();
  const [form] = Form.useForm();
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [projectGroupsLoading, setProjectGroupsLoading] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; username: string }>>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  // 调试：权限/范围
  const [rawPerms, setRawPerms] = useState<any>(null);
  const apiBase = `${(process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '')}/api/v1`;
  const [permProbe, setPermProbe] = useState<Record<string, boolean | 'error'>>({});
  const [upsDiag, setUpsDiag] = useState<any>(null);
  const [checkPermHttp, setCheckPermHttp] = useState<{
    basic?: { status?: number; ok?: boolean; sample?: string };
    withLevel?: { status?: number; ok?: boolean; sample?: string };
  }>({});
  const [checkPermHttpGet, setCheckPermHttpGet] = useState<{
    basic?: { status?: number; ok?: boolean; sample?: string };
    withLevel?: { status?: number; ok?: boolean; sample?: string };
  }>({});

  // 获取项目组列表
  const fetchProjectGroups = async () => {
    try {
      setProjectGroupsLoading(true);
      const response = await getProjectGroupList({ page: 1, page_size: 100 });
      setProjectGroups(response.data.results);
      console.log('📋 加载项目组列表完成:', response.data.results.length, '个项目组');
    } catch (error) {
      console.error('获取项目组列表失败:', error);
      message.error('获取项目组列表失败');
    } finally {
      setProjectGroupsLoading(false);
    }
  };

  // 获取客户列表
  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const customersList = await getCustomerList({ onlyMain: true });
      setCustomers(customersList);
      console.log('👥 加载客户列表完成:', customersList.length, '个客户');
    } catch (error) {
      console.error('获取客户列表失败:', error);
      message.error('获取客户列表失败');
    } finally {
      setCustomersLoading(false);
    }
  };

  // 初始化加载项目组列表；客户列表按策略可见性决定是否加载
  useEffect(() => {
    console.info('[ProjectList] init/load: hideCustomer=', hideCustomer);
    fetchProjectGroups();
    if (!hideCustomer) {
      console.info('[ProjectList] fetchCustomers() triggered');
      fetchCustomers();
    } else {
      console.info('[ProjectList] skip fetchCustomers due to hideCustomer');
    }
  }, [hideCustomer]);

  // 权限/范围调试：拉取用户权限、check-permission、UPS 诊断（resource=project, action=read）
  useEffect(() => {
    (async () => {
      try {
        const p = await permissionServiceAPI.getUserPermissions();
        setRawPerms(p);
      } catch (e) {
        setRawPerms(null);
      }

      const keys = ['project:read', 'projects:read', 'project_read', 'project:Read'];
      const results: Record<string, boolean | 'error'> = {};
      for (const k of keys) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const ok = await permissionServiceAPI.checkPermission(k);
          results[k] = ok;
        } catch (e) {
          results[k] = 'error';
        }
      }
      try {
        const okWithLevel = await permissionServiceAPI.checkPermission('project:read', 'project');
        results['project:read@level=project'] = okWithLevel;
      } catch (e) {
        results['project:read@level=project'] = 'error';
      }
      setPermProbe(results);

      // 直接 HTTP 探测（POST）
      try {
        const resp = await fetch(`${apiBase}/users/check-permission/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ permission: 'project:read' }),
        });
        const text = await resp.text();
        setCheckPermHttp(prev => ({ ...prev, basic: { status: resp.status, ok: resp.ok, sample: text.slice(0, 400) } }));
      } catch (e) {
        setCheckPermHttp(prev => ({ ...prev, basic: { status: undefined, ok: false, sample: '请求失败' } }));
      }
      try {
        const resp = await fetch(`${apiBase}/users/check-permission/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ permission: 'project:read', level: 'project' }),
        });
        const text = await resp.text();
        setCheckPermHttp(prev => ({ ...prev, withLevel: { status: resp.status, ok: resp.ok, sample: text.slice(0, 400) } }));
      } catch (e) {
        setCheckPermHttp(prev => ({ ...prev, withLevel: { status: undefined, ok: false, sample: '请求失败' } }));
      }

      // 直接 HTTP 探测（GET）
      try {
        const resp = await fetch(`${apiBase}/users/check-permission/?permission=project:read`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const text = await resp.text();
        setCheckPermHttpGet(prev => ({ ...prev, basic: { status: resp.status, ok: resp.ok, sample: text.slice(0, 400) } }));
      } catch (e) {
        setCheckPermHttpGet(prev => ({ ...prev, basic: { status: undefined, ok: false, sample: '请求失败' } }));
      }
      try {
        const resp = await fetch(`${apiBase}/users/check-permission/?permission=project:read&level=project`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const text = await resp.text();
        setCheckPermHttpGet(prev => ({ ...prev, withLevel: { status: resp.status, ok: resp.ok, sample: text.slice(0, 400) } }));
      } catch (e) {
        setCheckPermHttpGet(prev => ({ ...prev, withLevel: { status: undefined, ok: false, sample: '请求失败' } }));
      }

      // UPS 诊断：resource=project, action=read（输出 scope 与 scope_rules）
      try {
        const resp = await fetch(`${apiBase}/permission-management/ups-diagnose/?resource=project&action=read`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const json = await resp.json().catch(async () => ({ raw: await resp.text() }));
        setUpsDiag({ status: resp.status, ok: resp.ok, body: json });
      } catch (e) {
        setUpsDiag({ status: undefined, ok: false, body: '请求失败' });
      }
    })();
  }, [apiBase]);

  // 使用 useTableQuery Hook - 实现"单一数据源 + 强制同步 + 解耦触发"架构
  const {
    state,
    data,
    loading,
    error,
    actions,
    paginationConfig
  } = useTableQuery<ProjectFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 10, total: 0 },
      filters: { search: '', project_type: '', project_status: '', project_group: '', customer: '', missing_inventory_config: false },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,           // 启用URL状态同步
      urlKeys: ['pagination', 'filters', 'sorting']  // 同步分页、筛选、排序到URL
    },
    fetchData: async (state) => {
      const { pagination, filters, sorting } = state;
      
      console.log('🔍 fetchData 调用参数:', {
        pagination,
        filters,
        sorting
      });
      
      // 构建API参数
      const params: ProjectListParams = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ordering: sorting.order === 'desc' ? `-${sorting.field}` : sorting.field,
        search: filters.search || '',
        project_type: filters.project_type || '',
        project_status: filters.project_status || '',
        project_group: filters.project_group ? parseInt(filters.project_group) : undefined,
        customer: !hideCustomer && filters.customer ? parseInt(filters.customer) : undefined,
        missing_inventory_config:
          filters.missing_inventory_config === true ||
          String((filters as any).missing_inventory_config).toLowerCase() === 'true',
      };
      if (hideCustomer && filters.customer) {
        console.info('[ProjectList] remove customer filter from params due to hideCustomer');
      }
      
      console.log('🔍 API参数:', params);
      
      // 调用API
      const response = await getProjectList(params);
      
      console.log('📊 API响应:', response);
      
      return {
        results: response.data.results,
        count: response.data.count
      };
    }
  });

  // 当策略隐藏客户信息时，清理 URL/状态中的客户筛选，防止无效筛选参数外传
  useEffect(() => {
    if (hideCustomer && state.filters.customer) {
      console.info('[ProjectList] clearing customer filter in state due to hideCustomer');
      actions.updateFilters({ customer: '' });
    }
  }, [hideCustomer, state.filters.customer]);

  // 表格变化处理（分页、排序等）
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log('📊 表格变化:', { pagination, filters, sorter });
    
    // 处理分页变化
    if (pagination.current !== state.pagination.current) {
      console.log('📄 页码变化:', pagination.current, '->', state.pagination.current);
      actions.updatePagination({ current: pagination.current });
    }
    
    if (pagination.pageSize !== state.pagination.pageSize) {
      console.log('📏 每页数量变化:', pagination.pageSize, '->', state.pagination.pageSize);
      actions.updatePagination({ current: 1, pageSize: pagination.pageSize });
    }
    
    // 处理排序变化
    if (sorter && sorter.field) {
      console.log('排序变化:', sorter);
      actions.updateSorting({
        field: sorter.field,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    }
  };

  // 操作处理函数
  const location = useLocation();

  const handleCreateProject = () => {
    const basePath = user?.role === 'admin' ? '/dashboard/admin' : user?.role === 'staff' ? '/dashboard/staff' : '/dashboard/customer';
    navigate(`${basePath}/projects/create`);
  };

  const handleProjectAddress = (record: Project) => {
    console.log('[ProjectList][Address] click', {
      now: new Date().toISOString(),
      locationPath: location.pathname,
      userRole: user?.role,
      projectId: record.id,
      projectCode: (record as any)?.project_code,
      historyLen: window.history.length
    });
    const basePath = location.pathname.includes('/dashboard/admin')
      ? '/dashboard/admin'
      : location.pathname.includes('/dashboard/staff')
        ? '/dashboard/staff'
        : '/dashboard/customer';
    const url = `${basePath}/projects/${record.id}/address`;
    console.log('[ProjectList][Address] navigate', { basePath, url });
    // 统一使用内标签（由 TabsManager 根据路径自动创建/激活）
    navigate(url);
  };

  const handleProjectInventory = (record: Project) => {
    const basePath = location.pathname.includes('/dashboard/admin')
      ? '/dashboard/admin'
      : location.pathname.includes('/dashboard/staff')
        ? '/dashboard/staff'
        : '/dashboard/customer';
    const params = new URLSearchParams();
    params.set('filter_project', String(record.id));
    params.set('from_project_list', '1');
    params.set('jump_ts', String(Date.now()));
    navigate(`${basePath}/inventory/current?${params.toString()}`);
  };

  const handleProjectOrders = (record: Project) => {
    const basePath = location.pathname.includes('/dashboard/admin')
      ? '/dashboard/admin'
      : location.pathname.includes('/dashboard/staff')
        ? '/dashboard/staff'
        : '/dashboard/customer';
    const jumpTs = String(Date.now());
    const params = new URLSearchParams();
    params.set('filter_project', String(record.id));
    params.set('from_project_list', '1');
    params.set('jump_ts', jumpTs);
    try {
      const payload = { projectId: String(record.id), jumpTs };
      sessionStorage.setItem('instruction_project_quick_jump', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('instruction:project-quick-jump', { detail: payload }));
    } catch {}
    navigate(`${basePath}/instructions/list?${params.toString()}`);
  };

  const handleProjectBills = (record: Project) => {
    const basePath = location.pathname.includes('/dashboard/admin')
      ? '/dashboard/admin'
      : location.pathname.includes('/dashboard/staff')
        ? '/dashboard/staff'
        : '/dashboard/customer';
    const jumpTs = String(Date.now());
    const params = new URLSearchParams();
    params.set('filter_project', String(record.id));
    params.set('from_project_list', '1');
    params.set('jump_ts', jumpTs);
    try {
      const payload = { projectId: String(record.id), jumpTs };
      sessionStorage.setItem('bills_project_quick_jump', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('bills:project-quick-jump', { detail: payload }));
    } catch {}
    navigate(`${basePath}/finance/bills?${params.toString()}`);
  };

  const handleBasicConfigManage = (record: Project) => {
    if (!record.has_inventory_config || !record.inventory_config_id) {
      message.warning('该项目尚未创建基础配置');
      return;
    }
    const basePath = location.pathname.includes('/dashboard/admin')
      ? '/dashboard/admin'
      : location.pathname.includes('/dashboard/staff')
        ? '/dashboard/staff'
        : '/dashboard/customer';
    navigate(`${basePath}/projects/inventory-config/${record.inventory_config_id}`);
  };

  return (
    <div className="project-list">
      <Card>
        {/* 页面标题和统计信息 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>项目列表</Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  共 {state.pagination.total} 条记录
                </Tag>
              </div>
            </div>
            
            {/* 右侧操作按钮 */}
            <div className="flex items-center space-x-2">
              {/* 所有角色均显示按钮，按权限禁用并提示 */}
              <Tooltip title={canCreateProject ? '' : '无权限'}>
                  <span>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateProject}
                      disabled={!canCreateProject}
                    >
                      创建项目
                    </Button>
                  </span>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* 筛选组件 */}
        <ProjectFilter
          filters={state.filters}
          onFiltersChange={(newFilters) => {
            console.log('🔍 筛选条件变化:', newFilters);
            actions.updateFilters(newFilters);
            // 筛选条件变化时，重置到第一页
            actions.updatePagination({ current: 1 });
          }}
          onReset={() => {
            console.log('🔄 重置筛选条件');
            actions.reset();
          }}
          loading={loading}
          projectGroups={projectGroups}
          projectGroupsLoading={projectGroupsLoading}
          customers={hideCustomer ? [] : customers}
          customersLoading={hideCustomer ? false : customersLoading}
        />
        
        {/* 数据表格 */}
        <ProjectTable
          data={data}
          loading={loading}
          pagination={paginationConfig}
          onTableChange={handleTableChange}
          onAddress={handleProjectAddress}
          onBasicConfigManage={handleBasicConfigManage}
          onInventory={handleProjectInventory}
          onOrders={handleProjectOrders}
          onBills={handleProjectBills}
        />

      </Card>
    </div>
  );
};

export default ProjectList;
